import { useCallback, useMemo, useRef, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { t } from '@lingui/macro';
import * as yup from 'yup';
import { validatePhoneNumber } from 'utils/phoneFormatter';
import { codeToCountry } from 'utils/codeToCountry';
import parseBackendErrorMessage from 'utils/parseBackendErrorMessage';
import noop from 'lodash/noop';

import { Modal } from '@decisiv/ui-components';
import { useNotifications } from '@decisiv/ui-components/lib/components/Notifications';
import PlusCircle from '@decisiv/iconix/lib/components/PlusCircle';

import { createForm, FormHandler } from 'components/Form';

import Content from './Content';
import { useCreateManagedAccountMutation } from './useCreateManagedAccountMutation';
import useCreateAccountCapability from './useCreateAccountCapability';

import { ContentColumn, ErrorMessageBanner } from '../styles';

const validationSchema = yup
  .object({
    sentinelTeamId: yup.string().required(t`Team is required`),
    name: yup.string().required(t`Name is required`),
    city: yup.string().required(t`City is required`),
    state: yup.string().required(t`State is required`),
    address1: yup.string().required(t`Address Line 1 is required`),
    address2: yup.string(),
    postalCode: yup.string().required(t`Postal Code is required`),
    country: yup.string().required(t`Country is required`),
    countryCode: yup.string(),
    phoneNumber: yup
      .string()
      .nullable()
      .test(
        'is-valid-phone',
        t`Invalid mobile phone number`,
        (value, context): boolean =>
          !value ||
          validatePhoneNumber(
            value,
            codeToCountry(context.parent.countryCode),
            true,
          ),
      ),
    email: yup.string().email(t`Enter a valid email address.`),
  })
  .test(
    'contact-info',
    t`Provide at least an email address or phone number.`,
    (values) => {
      const { phoneNumber, email } = values;
      return !!(phoneNumber || email);
    },
  )
  .required();

type FormData = yup.InferType<typeof validationSchema>;

const { Form, FormProvider } = createForm<FormData>();

interface CreateAccountModalProps {
  onClose: () => void;
}

const FormContent = ({ onClose }: CreateAccountModalProps) => {
  const {
    formState: { isValid },
  } = useFormContext();
  const { notify } = useNotifications();
  const { capability: createAccountCapability } = useCreateAccountCapability();
  const isDenied = createAccountCapability?.allowed === false;

  const [errorMessage, setErrorMessage] = useState<{
    title: string;
    description: string;
  } | null>(null);

  const formRef = useRef<FormHandler<FormData>>(null);

  const handleTriggerForm = useCallback(async () => {
    await formRef.current?.submit();
  }, []);

  const showError = useCallback(
    (description: string) =>
      setErrorMessage({ title: t`Failed to create account.`, description }),
    [],
  );

  const [createManagedAccount, { loading: creating }] =
    useCreateManagedAccountMutation({
      refetchQueries: ['ManagedPlatformAccounts'],
      awaitRefetchQueries: true,
    });

  const handleOnSubmit = useCallback(
    async (data: FormData) => {
      setErrorMessage(null);
      try {
        const sanitizedPhone = data.phoneNumber
          ? data.phoneNumber.replace(/\D/g, '')
          : '';
        const fullPhone = sanitizedPhone
          ? `${data.countryCode}${sanitizedPhone}`
          : '';

        await createManagedAccount({
          variables: {
            attributes: {
              sentinelTeamId: data.sentinelTeamId,
              name: data.name,
              city: data.city,
              state: data.state,
              address1: data.address1,
              address2: data.address2 || '',
              postalCode: data.postalCode,
              country: data.country,
              phone: fullPhone,
              email: data.email,
            },
          },
        });

        notify({
          duration: 5000,
          intent: 'success',
          title: t`Account successfully created.`,
          onClose: noop,
        } as any);

        onClose();
      } catch (error) {
        showError(
          parseBackendErrorMessage(error) ||
            t`An unexpected error occurred. Please try again.`,
        );
      }
    },
    [notify, createManagedAccount, onClose, showError],
  );

  const handleClose = useCallback(() => {
    setErrorMessage(null);
    onClose();
  }, [onClose]);

  const modalActions = useMemo(
    () =>
      [
        {
          text: t`Create Account`,
          loading: creating,
          disabled: !isValid || isDenied,
          onClick: handleTriggerForm,
        },
        {
          text: t`Cancel`,
          onClick: handleClose,
        },
      ] as [any, any],
    [creating, isValid, isDenied, handleTriggerForm, handleClose],
  );

  return (
    <Modal
      actions={modalActions}
      title={t`Create Account`}
      intent="information"
      icon={PlusCircle}
      color="information"
      visible
    >
      <ContentColumn>
        {errorMessage && (
          <ErrorMessageBanner
            intent="danger"
            size="medium"
            title={errorMessage.title}
          >
            {errorMessage.description}
          </ErrorMessageBanner>
        )}
        <Form flex={1} ref={formRef} onSubmit={handleOnSubmit}>
          <Content capability={createAccountCapability} />
        </Form>
      </ContentColumn>
    </Modal>
  );
};

interface CreateAccountModalWrapperProps {
  onClose: () => void;
}

const CreateAccountModal = ({ onClose }: CreateAccountModalWrapperProps) => {
  const initialValues: FormData = useMemo(
    () => ({
      sentinelTeamId: '',
      name: '',
      city: '',
      state: '',
      address1: '',
      address2: '',
      postalCode: '',
      country: 'US',
      countryCode: '+1',
      phoneNumber: '',
      email: '',
    }),
    [],
  );

  return (
    <FormProvider
      initialValues={initialValues}
      validationSchema={validationSchema}
    >
      <FormContent onClose={onClose} />
    </FormProvider>
  );
};

export default CreateAccountModal;
